/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import {
  computeVideoEventMarkers,
  MARKER_SEEK_OFFSET,
  VideoEventMarker,
} from '@sportninja/common/utils/games/videoEventMarkers'
import type { ScoreboardEvent } from '@sportninja/common/utils/games/scoreboardSync'

// Marker dots sit on the native progress bar. They fade in/out with the
// controls via the parent's `video-markers-visible` class (toggled by mouse
// activity on the video container).
const container = css`
  position: absolute;
  bottom: 14px;
  left: 0;
  right: 0;
  height: 16px;
  pointer-events: none;
  z-index: 11;
  opacity: 0;
  transition: opacity 0.3s ease;
`

const markerDot = css`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.WHITE};
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.15s ease, transform 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);

  &:hover {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.4);
  }
`

const tooltipStyle = css`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 4px;
  background: rgba(0, 0, 0, 0.8);
  color: ${colors.WHITE};
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
`

const markerWithTooltip = css`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;

  &:hover span {
    opacity: 1;
  }
`

/**
 * CSS to apply on the video container (parent) to:
 * 1. Show/hide markers with the native controls (on hover)
 * 2. Style the native progress bar with the primary brand color
 */
export const videoContainerMarkerStyles = css`
  /* Show markers when hovering the video container */
  &:hover [data-video-markers] {
    opacity: 1;
  }

  /* Style native progress bar - Chrome/Safari */
  video::-webkit-media-controls-timeline {
    accent-color: ${colors.PRIMARY};
  }

  /* Style native progress bar - Firefox */
  video {
    accent-color: ${colors.PRIMARY};
  }
`

interface VideoEventMarkersProps {
  videoRef: { current: HTMLVideoElement | null }
  scoreboardEvents: ScoreboardEvent[] | null | undefined
  streamStartedAt: string | null | undefined
  isLive: boolean
}

const VideoEventMarkers = ({
  videoRef,
  scoreboardEvents,
  streamStartedAt,
  isLive,
}: VideoEventMarkersProps) => {
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const video = videoRef?.current
    if (!video || isLive) {
      return
    }

    const handleMetadata = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration)
      }
    }

    // Check if already loaded
    if (video.duration && isFinite(video.duration)) {
      setDuration(video.duration)
    }

    video.addEventListener('loadedmetadata', handleMetadata)
    video.addEventListener('durationchange', handleMetadata)
    return () => {
      video.removeEventListener('loadedmetadata', handleMetadata)
      video.removeEventListener('durationchange', handleMetadata)
    }
  }, [videoRef, isLive])

  if (isLive || duration <= 0) {
    return null
  }

  const markers = computeVideoEventMarkers(
    scoreboardEvents,
    streamStartedAt,
    duration
  )

  if (markers.length === 0) {
    return null
  }

  const handleMarkerClick = (marker: VideoEventMarker) => {
    const video = videoRef?.current
    if (video) {
      video.currentTime = Math.max(0, marker.positionSec - MARKER_SEEK_OFFSET)
    }
  }

  return (
    <div css={container} data-video-markers>
      {markers.map((marker, idx) => (
        <div
          key={`${marker.eventType}-${idx}`}
          css={markerWithTooltip}
          style={{ left: `${marker.positionPercent}%` }}
          onClick={() => handleMarkerClick(marker)}
        >
          <div css={markerDot} />
          <span css={tooltipStyle}>{marker.label}</span>
        </div>
      ))}
    </div>
  )
}

export default VideoEventMarkers
